import { useEffect, useState } from "react";
import { PaperPlaneTilt, EnvelopeSimple, Paperclip, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

/**
 * Generic "Send via Gmail" modal.
 *
 * Props:
 *  - open, onOpenChange
 *  - endpoint            POST endpoint (e.g. "/oauth/gmail/send/proposal/<id>")
 *  - defaultTo           string | string[] | ""  pre-filled recipient
 *  - defaultSubject      string
 *  - defaultBody         string (plain text; converted to HTML <p> blocks)
 *  - attachmentName      string  (just for display)
 *  - kindLabel           string  ("Proposal" | "Quotation" | ...)
 *  - onSent              () => void  invoked after a successful send
 */
export default function SendGmailModal({
  open, onOpenChange,
  endpoint,
  defaultTo = "",
  defaultSubject = "",
  defaultBody = "",
  attachmentName = "document.pdf",
  kindLabel = "Document",
  onSent,
}) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [gmailStatus, setGmailStatus] = useState(null); // null = unknown, {connected, email}

  // Reset + check Gmail status whenever the modal opens
  useEffect(() => {
    if (!open) return;
    const t = Array.isArray(defaultTo) ? defaultTo.join(", ") : (defaultTo || "");
    setTo(t);
    setCc("");
    setBcc("");
    setSubject(defaultSubject);
    setBody(defaultBody);
    setSending(false);
    api.get("/oauth/gmail/status")
      .then((r) => setGmailStatus(r.data))
      .catch(() => setGmailStatus({ connected: false }));
    // eslint-disable-next-line
  }, [open]);

  const toEmails = (s) => s.split(/[,;\n]+/).map((e) => e.trim()).filter(Boolean);

  // Auto-build HTML body from plain text (paragraphs preserved)
  const buildHtml = (text) => {
    const escaped = (text || "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return escaped
      .split(/\n{2,}/)
      .map((p) => `<p style="margin:0 0 12px 0;line-height:1.55;color:#0A0A0A;font-size:14px;font-family:Arial,sans-serif">${p.replace(/\n/g, "<br/>")}</p>`)
      .join("");
  };

  const send = async () => {
    if (!gmailStatus?.connected) {
      toast.error("Gmail not connected. Go to Admin → Connect Gmail.");
      return;
    }
    const toList = toEmails(to);
    if (toList.length === 0) {
      toast.error("Add at least one recipient");
      return;
    }
    if (!subject.trim()) {
      toast.error("Subject required");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(endpoint, {
        to: toList,
        cc: toEmails(cc),
        bcc: toEmails(bcc),
        subject,
        body_html: buildHtml(body),
      });
      toast.success(`Sent to ${toList.length} recipient${toList.length === 1 ? "" : "s"}`);
      if (onSent) onSent(data);
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gmail send failed";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none max-h-[92vh] overflow-y-auto" data-testid="send-gmail-modal">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <EnvelopeSimple size={22} weight="bold" className="text-[#D90429]" />
            Send {kindLabel} via Gmail
          </DialogTitle>
        </DialogHeader>

        {gmailStatus && !gmailStatus.connected && (
          <div className="border border-[#D90429] bg-[#FFE5E9]/40 p-3 text-sm" data-testid="gmail-disconnected-warning">
            Gmail isn&apos;t connected yet. Go to <b>Admin → Users</b> and click <b>Connect Gmail</b> first.
          </div>
        )}

        {gmailStatus?.connected && (
          <div className="border border-black/10 bg-[#FAFBFC] p-2.5 text-xs flex items-center gap-2" data-testid="gmail-sender-info">
            <span className="w-2 h-2 rounded-full bg-[#0D9488]" />
            Sending as <b className="font-mono-shelv">{gmailStatus.email}</b>
          </div>
        )}

        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">To *</Label>
            <Input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="client@example.com, second@example.com"
              className="rounded-none"
              data-testid="gmail-to-input"
            />
            <div className="text-[10px] text-[#717680] mt-0.5 font-mono-shelv tracking-widest uppercase">Separate multiple addresses with commas</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Cc</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} className="rounded-none" data-testid="gmail-cc-input" />
            </div>
            <div>
              <Label className="text-xs">Bcc</Label>
              <Input value={bcc} onChange={(e) => setBcc(e.target.value)} className="rounded-none" data-testid="gmail-bcc-input" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Subject *</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-none" data-testid="gmail-subject-input" />
          </div>

          <div>
            <Label className="text-xs">Message</Label>
            <Textarea
              rows={9}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="rounded-none font-mono-shelv text-[13px] leading-relaxed"
              data-testid="gmail-body-input"
            />
            <div className="text-[10px] text-[#717680] mt-0.5">Your Shelvestech signature is added automatically.</div>
          </div>

          <div className="flex items-center gap-2 border border-black/10 bg-[#FAFBFC] p-2.5 text-xs" data-testid="gmail-attachment-pill">
            <Paperclip size={14} weight="bold" className="text-[#D90429]" />
            <span className="font-mono-shelv">{attachmentName}</span>
            <span className="text-[#717680] ml-auto">auto-attached PDF</span>
          </div>
        </div>

        <DialogFooter className="mt-3">
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 border border-black/15 text-sm hover:bg-black/5"
            data-testid="gmail-cancel"
          >
            <X size={14} weight="bold" className="inline mr-1" /> Cancel
          </button>
          <button
            onClick={send}
            disabled={sending || !gmailStatus?.connected}
            data-testid="gmail-send-button"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#D90429] text-white text-sm font-bold hover:bg-[#EF233C] disabled:opacity-40"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            {sending ? "Sending…" : "Send via Gmail"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
